import React, { forwardRef, useId } from 'react';
import { cn } from '@/lib/cn';
import { IconAlertCircleFilled, IconChevronDown } from '@repo/svgs';

export type DropdownFieldVariant = 'default' | 'filled';

export interface DropdownFieldProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  label?: string;
  placeholder?: string;
  selectedLabel?: React.ReactNode;
  variant?: DropdownFieldVariant;
  alert?: boolean;
  helperText?: React.ReactNode;
  helperIcon?: React.ReactNode;
  isOpen?: boolean;
  wrapperClassName?: string;
}

export const DropdownField = forwardRef<HTMLButtonElement, DropdownFieldProps>(
  function DropdownField(
    {
      label,
      placeholder = 'Select…',
      selectedLabel,
      variant = 'default',
      alert = false,
      helperText,
      helperIcon,
      isOpen,
      disabled,
      className,
      wrapperClassName,
      ...props
    },
    ref,
  ) {
    const isFilled = variant === 'filled';
    const hasValue = selectedLabel != null;
    const id = useId();
    const helperId = helperText ? `${id}-helper` : undefined;
    const showAlert = alert && !disabled;

    const triggerClasses = cn(
      // layout
      'relative inline-flex items-center justify-between',
      // size / gaps
      'h-11 w-full rounded-full px-4 gap-2',
      // text / styles
      'border focus-visible:outline-none',
      // Active/focus ring (internal) + avoid double border
      isOpen ? 'ring-2 ring-inset ring-accent border-transparent' : null,
      'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent focus-visible:border-transparent',
      // Disabled / alert / default border
      disabled ? 'cursor-not-allowed opacity-60' : null,
      showAlert
        ? 'border-alert focus-visible:ring-alert focus-visible:border-transparent'
        : 'border-secondary',
      // background
      'bg-primary hover:bg-primary-hover',
      className,
    );

    const textClasses = cn(
      // layout
      'truncate text-left',
      // size / gaps
      'w-full',
      // text / styles
      'text-body-2',
      disabled ? 'text-tertiary' : hasValue || isFilled ? 'text-primary' : 'text-tertiary',
    );

    const chevronClasses = cn(
      // layout
      'shrink-0 inline-flex items-center justify-center',
      // size / gaps
      'size-3',
      // text / styles
      'icon-secondary transition-transform duration-150',
      isOpen ? 'rotate-180' : '',
    );

    return (
      <div className={cn('flex w-full flex-col gap-1', wrapperClassName)}>
        {label ? <label className={cn('text-body-2-high text-secondary')}>{label}</label> : null}
        <button
          ref={ref}
          type="button"
          className={triggerClasses}
          disabled={disabled}
          aria-invalid={showAlert || undefined}
          aria-describedby={helperId}
          {...props}
        >
          <span className={textClasses}>{hasValue ? selectedLabel : placeholder}</span>
          <span aria-hidden className={cn('inline-flex shrink-0 items-center justify-center')}>
            <IconChevronDown className={chevronClasses} />
          </span>
        </button>

        {helperText ? (
          <div className={cn('flex items-center gap-1')} id={helperId}>
            {showAlert
              ? (helperIcon ?? <IconAlertCircleFilled className="h-4 w-4 icon-alert" />)
              : null}
            <span
              className={cn('flex-1 text-caption', showAlert ? 'text-alert' : 'text-secondary')}
            >
              {helperText}
            </span>
          </div>
        ) : null}
      </div>
    );
  },
);

export default DropdownField;
